import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { Trash2 } from 'lucide-react'
import {
  Button,
  Select,
  cn,
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type Announcements,
  type DraggableAttributes,
  type UseDraggableResult,
} from '@ludus/ui'
import { asPipelineId, type PipelineId } from '@ludus/core'
import {
  fetchPipeline,
  fetchPipelineStatuses,
  updateStatus,
  PipelineApiError,
} from '@/lib/api/pipelines.ts'
import { SourceSection } from '../ConnectSource/SourceSection.tsx'
import { FlowsSection } from '../Flows/FlowsSection.tsx'
import { RemovalSection } from '../Removal/RemovalSection.tsx'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import { getStageColor, type StageKind } from '@/shared/utils/stageColors.ts'
import { pipelineAccent } from '@/shared/utils/pipelineAccent.ts'
import { MOCK_SETTINGS_PIPELINE, MOCK_SETTINGS_STATUSES } from '../data/settingsMock.ts'
import { NewStageDialog } from '../components/NewStageDialog.tsx'
import { RetireStageDialog } from '../components/RetireStageDialog.tsx'
import {
  LIFECYCLE_PHASES,
  ENGINE_ROLES,
  type EngineRole,
  type InternalStatus,
  type LifecyclePhase,
  type StatusId,
} from '../types.ts'

/**
 * Per-pipeline settings (SBO-60 / ADR-0003). Reachable from the catalogue gear
 * icon. Edits the **internal → canonical** layer: each internal status rolls up
 * to exactly one lifecycle phase (Axis A) and carries one engine role (Axis B).
 *
 * The page is laid out **by phase** so the many-to-one is visible at a glance —
 * the five canonical phases are fixed buckets, and a pipeline's statuses sit
 * under whichever phase they map to (several statuses under one phase is the
 * whole point). Re-phasing a status moves its card between buckets; its engine
 * role is a badge toggle on the card. The role is the distribution trigger
 * config the console owns; *consuming* it stays deferred engine work.
 *
 * A stage's name is a free-form label nothing keys off, so renaming is an inline
 * edit in the same draft overlay. Retiring one is not: the engine refuses a
 * stage that still holds leads, so the dialog asks where they go and moves them
 * first.
 *
 * Self-contained route like `LeadDetailPage` (reads `useParams`, its own lean
 * fetches). Persists with the `forms compute, don't copy` convention — edits
 * live in a draft overlay, and an explicit Save diffs the overlay and PATCHes
 * only the changed statuses, then revalidates the shared `/statuses` cache so
 * the kanban stays consistent.
 */

/** Phase → stage-palette bucket, so the taxonomy reuses the kanban's hues. */
const PHASE_KIND: Record<LifecyclePhase, StageKind> = {
  intake: 'intake',
  qualification: 'cool',
  proposal: 'warm',
  won: 'won',
  lost: 'lost',
}

/**
 * Active-segment styling per engine role (the role picker is a segmented
 * control — a bordered track so the pills read as one bounded control instead
 * of blending into the rest of the card). Tone pairs mirror `Badge`; the
 * distribution trigger gets the solid brand fill to stand out.
 */
const ROLE_ACTIVE: Record<EngineRole, string> = {
  awaiting_distribution: 'bg-primary text-primary-foreground shadow-sm',
  terminal_won: 'bg-toast-success text-toast-success-text shadow-sm',
  terminal_lost: 'bg-toast-error text-toast-error-text shadow-sm',
  arrived: 'bg-toast-info text-toast-info-text shadow-sm',
  none: 'bg-foreground/10 text-foreground shadow-sm',
}

type StatusEdit = { name?: string; lifecyclePhase?: LifecyclePhase; engineRole?: EngineRole }
type SaveMessage = { tone: 'saved' | 'error'; text: string } | null

/** The `t` surface the extracted card/bucket components need — structural, so we
 *  don't drag i18next's full `TFunction` generics through the props. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/**
 * DEV PREVIEW (SBO-60) — disposable.
 *
 * `true`  → the form renders from `data/settingsMock.ts`; no engine / D1 / seed /
 *           bearer token needed. Save is a local no-op. Use it to eyeball the UI
 *           with a console-only `pnpm -F @ludus/console dev`, then open
 *           `/pipelines/<anything>/settings` directly (the gear in the catalogue
 *           still needs the engine to list pipelines).
 * `false` → real path: SWR fetches + PATCH against the engine. **Ship `false`.**
 *
 * To remove this preview scaffold entirely:
 *   1. delete `src/features/pipelines/data/settingsMock.ts`,
 *   2. drop its import above,
 *   3. delete this flag and inline the `!PREVIEW_MOCK` / `PREVIEW_MOCK ? …`
 *      branches (the gate block, the `pipeline`/`statuses` ternaries, and the
 *      early return in `onSave`).
 */
const PREVIEW_MOCK = false

export function PipelineSettingsPage() {
  const { t } = useTranslation()
  const params = useParams<{ pipelineId: string }>()
  const pipelineId = params.pipelineId ? asPipelineId(params.pipelineId) : null

  const pipelineSwr = useSWR(!PREVIEW_MOCK && pipelineId ? `/pipelines/${pipelineId}` : null, () =>
    fetchPipeline(pipelineId as PipelineId),
  )
  const statusesSwr = useSWR(
    !PREVIEW_MOCK && pipelineId ? `/pipelines/${pipelineId}/statuses` : null,
    () => fetchPipelineStatuses(pipelineId as PipelineId),
  )

  const [edits, setEdits] = useState<Record<string, StatusEdit>>({})
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<SaveMessage>(null)
  const [addingStage, setAddingStage] = useState(false)
  const [renamingId, setRenamingId] = useState<StatusId | null>(null)
  const [retiring, setRetiring] = useState<InternalStatus | null>(null)

  // In preview-mock mode the URL/loading/error gates don't apply — the form
  // renders straight from the disposable fixture.
  if (!PREVIEW_MOCK) {
    if (!pipelineId) {
      return (
        <CenteredPanel
          title={t('pages.pipelines.settings.invalid.title', { defaultValue: 'Invalid URL' })}
          body={t('pages.pipelines.settings.invalid.body', {
            defaultValue: 'The settings URL is missing a pipeline id.',
          })}
        />
      )
    }

    if (pipelineSwr.isLoading || statusesSwr.isLoading) {
      return (
        <CenteredPanel
          title={t('pages.pipelines.settings.loading', { defaultValue: 'Loading settings…' })}
          muted
        />
      )
    }

    const error =
      (pipelineSwr.error as PipelineApiError | undefined) ??
      (statusesSwr.error as PipelineApiError | undefined)
    if (error) {
      return (
        <CenteredPanel
          title={t('pages.pipelines.settings.error.title', {
            defaultValue: 'Could not load settings',
          })}
          body={
            error.kind === 'not_found'
              ? t('pages.pipelines.settings.error.notFound', {
                  defaultValue: 'This pipeline is no longer available.',
                })
              : t('pages.pipelines.settings.error.generic', {
                  defaultValue: 'The engine returned an error while loading pipeline settings.',
                })
          }
          meta={error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined}
          tone="error"
        />
      )
    }
  }

  const pipeline = PREVIEW_MOCK ? MOCK_SETTINGS_PIPELINE : pipelineSwr.data
  const statuses = PREVIEW_MOCK ? MOCK_SETTINGS_STATUSES : statusesSwr.data
  if (!pipeline || !statuses) return null
  const accent = pipelineAccent(pipeline)
  const initial = pipeline.name.trim().charAt(0).toUpperCase() || '•'

  // Effective (draft-or-server) value of a field for a status.
  const nameOf = (s: InternalStatus): string => edits[s.id]?.name ?? s.name
  // `null` is an interaction pipeline's outcome: no lifecycle, rendered as «—».
  const phaseOf = (s: InternalStatus): LifecyclePhase | null =>
    edits[s.id]?.lifecyclePhase ?? s.lifecyclePhase
  const roleOf = (s: InternalStatus): EngineRole => edits[s.id]?.engineRole ?? s.engineRole

  const changed = statuses.filter(
    (s) =>
      nameOf(s).trim() !== s.name || phaseOf(s) !== s.lifecyclePhase || roleOf(s) !== s.engineRole,
  )
  const dirty = changed.length > 0
  const blankName = statuses.some((s) => nameOf(s).trim().length === 0)

  const phaseOptions = LIFECYCLE_PHASES.map((p) => ({
    value: p,
    label: t(`pages.pipelines.settings.phase.${p}`, { defaultValue: p }),
  }))

  const setEdit = (id: StatusId, patch: StatusEdit) => {
    setMessage(null)
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const onSave = async () => {
    if (!dirty || saving || blankName) return
    // Preview mode has no engine — just accept the edits so the form's
    // saved/cleared flow is still demonstrable.
    if (PREVIEW_MOCK) {
      setEdits({})
      setMessage({
        tone: 'saved',
        text: t('pages.pipelines.settings.saved', { defaultValue: 'Saved' }),
      })
      return
    }
    setSaving(true)
    setMessage(null)

    const results = await Promise.allSettled(
      changed.map((s) => {
        // An outcome on an interaction board has no phase to send.
        const phase = phaseOf(s)
        return updateStatus(pipelineId as PipelineId, s.id, {
          name: nameOf(s).trim(),
          ...(phase === null ? {} : { lifecyclePhase: phase }),
          engineRole: roleOf(s),
        })
      }),
    )
    const failedIds = changed.filter((_, i) => results[i].status === 'rejected').map((s) => s.id)

    // Pull the authoritative server state back so saved rows reflect the DB and
    // the kanban (same cache key) sees the change. Then drop edits for every
    // status that saved — failures keep their pending edit so Save can retry.
    await statusesSwr.mutate()
    setEdits((prev) => {
      const next: Record<string, StatusEdit> = {}
      for (const id of failedIds) if (prev[id]) next[id] = prev[id]
      return next
    })

    setSaving(false)
    if (failedIds.length === 0) {
      setMessage({
        tone: 'saved',
        text: t('pages.pipelines.settings.saved', { defaultValue: 'Saved' }),
      })
    } else {
      setMessage({
        tone: 'error',
        text: t('pages.pipelines.settings.partialError', {
          count: failedIds.length,
          defaultValue: 'Could not save {{count}} status(es). Please retry.',
        }),
      })
    }
  }

  // Announcement strings for the DnD live region. The primitive ships no i18n —
  // it calls these callbacks and reads back our localized strings.
  const dndAnnouncements: Announcements = {
    onDragStart: ({ active }) =>
      t('pages.pipelines.settings.dnd.announce.start', {
        name: String(active.data.name ?? ''),
        count: LIFECYCLE_PHASES.length,
        defaultValue:
          'Picked up {{name}}. Use the arrow keys to move between {{count}} phases, then space or enter to drop.',
      }),
    onDragOver: ({ over }) =>
      over
        ? t('pages.pipelines.settings.dnd.announce.over', {
            phase: t(`pages.pipelines.settings.phase.${String(over.data.phase)}`, {
              defaultValue: String(over.data.phase),
            }),
            defaultValue: 'Over {{phase}}.',
          })
        : '',
    onDragEnd: ({ active, over }) =>
      t('pages.pipelines.settings.dnd.announce.end', {
        name: String(active.data.name ?? ''),
        phase: over
          ? t(`pages.pipelines.settings.phase.${String(over.data.phase)}`, {
              defaultValue: String(over.data.phase),
            })
          : '',
        defaultValue: 'Dropped {{name}} in {{phase}}.',
      }),
    onDragCancel: ({ active }) =>
      t('pages.pipelines.settings.dnd.announce.cancel', {
        name: String(active.data.name ?? ''),
        phase: t(`pages.pipelines.settings.phase.${String(active.data.fromPhase)}`, {
          defaultValue: String(active.data.fromPhase),
        }),
        defaultValue: 'Cancelled. {{name}} returned to {{phase}}.',
      }),
  }

  return (
    <div className="flex w-full max-w-3xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <Breadcrumb
        items={[
          {
            label: t('pages.pipelines.settings.breadcrumb.pipelines', {
              defaultValue: 'Pipelines',
            }),
            to: '/pipelines',
          },
          { label: pipeline.name, to: `/pipelines/${pipeline.id}` },
          {
            label: t('pages.pipelines.settings.breadcrumb.settings', { defaultValue: 'Settings' }),
          },
        ]}
      />

      <header
        className="relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-background-secondary p-4"
        style={{ '--accent': accent.hex } as CSSProperties}
      >
        {/* Soft brand glow — radial wash from the pipeline accent (no blur, so
            no FPS cost), same identity language as the catalogue cards. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(120% 120% at 0% 0%, ${accent.glow} 0%, transparent 55%)`,
          }}
        />
        <span
          className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-base font-semibold"
          style={{
            backgroundColor: accent.tile,
            color: accent.hex,
            boxShadow: `inset 0 0 0 1px ${accent.ring}`,
          }}
        >
          {initial}
        </span>
        <div className="relative flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-foreground">
            {t('pages.pipelines.settings.title', { defaultValue: 'Pipeline settings' })}
          </h1>
          <p className="text-xs text-muted">
            {t('pages.pipelines.settings.subtitle', {
              defaultValue: 'Map each status onto its canonical lifecycle phase and engine role.',
            })}
          </p>
        </div>
        {statuses.length > 0 && (
          <AddStageButton className="relative ml-auto" onClick={() => setAddingStage(true)} />
        )}
      </header>

      <SourceSection pipelineId={pipelineId as PipelineId} origin={pipeline.origin} />

      <FlowsSection pipelineId={pipelineId as PipelineId} kind={pipeline.kind} />

      <RemovalSection pipeline={pipeline} />

      {/* Two-axis explainer — the page's only job is to make clear *why* a status
          carries both a phase (meaning) and a role (engine behaviour). */}
      <dl className="flex flex-col gap-2.5 rounded-lg border border-border/50 bg-background-secondary/40 p-3.5 text-xs">
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="shrink-0 font-semibold text-foreground">
            {t('pages.pipelines.settings.explainer.phaseTerm', { defaultValue: 'Lifecycle phase' })}
          </dt>
          <dd className="text-muted">
            {t('pages.pipelines.settings.explainer.phaseBody', {
              defaultValue:
                'A fixed, cross-pipeline taxonomy. Each status maps to exactly one phase — this powers analytics and forecasting across pipelines.',
            })}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
          <dt className="shrink-0 font-semibold text-foreground">
            {t('pages.pipelines.settings.explainer.roleTerm', { defaultValue: 'Engine role' })}
          </dt>
          <dd className="text-muted">
            {t('pages.pipelines.settings.explainer.roleBody', {
              defaultValue:
                'What distribution does when a lead enters the status. Two statuses in the same phase can carry different roles — that is why it is a separate choice.',
            })}
          </dd>
        </div>
      </dl>

      {statuses.length === 0 ? (
        <CenteredPanel
          title={t('pages.pipelines.settings.empty.title', { defaultValue: 'No statuses' })}
          body={t('pages.pipelines.settings.empty.body', {
            defaultValue:
              'This pipeline has no stages yet. Add the first one, or connect an external source to mirror the stages it already has.',
          })}
          muted
        >
          <AddStageButton onClick={() => setAddingStage(true)} />
        </CenteredPanel>
      ) : (
        <DndContext
          announcements={dndAnnouncements}
          keyboardInstructions={t('pages.pipelines.settings.dnd.instructions', {
            defaultValue:
              'Press space or enter to pick up. Use the arrow keys to move between phases. Press space or enter to drop, or escape to cancel.',
          })}
          onDragEnd={({ active, over }) => {
            // Cross-bucket regroup only: a drop onto a phase bucket re-phases the
            // status via the existing edit overlay. Same-bucket / no-target drops
            // are no-ops. Reusing `setEdit` means Save/persist is untouched.
            if (!over) return
            const target = over.data.phase as LifecyclePhase
            if (target === (active.data.fromPhase as LifecyclePhase)) return
            setEdit(active.id as StatusId, { lifecyclePhase: target })
          }}
        >
          <div className="flex flex-col gap-5">
            {LIFECYCLE_PHASES.map((phase) => {
              const color = getStageColor(0, 1, PHASE_KIND[phase]).active
              const inPhase = statuses.filter((s) => phaseOf(s) === phase)
              return (
                <PhaseBucket
                  key={phase}
                  phase={phase}
                  color={color}
                  count={inPhase.length}
                  label={t(`pages.pipelines.settings.phase.${phase}`, { defaultValue: phase })}
                >
                  {inPhase.length === 0 ? (
                    <li className="px-2.5 py-2 text-xs text-muted/50">
                      {t('pages.pipelines.settings.phaseEmpty', {
                        defaultValue: 'No statuses roll up to this phase',
                      })}
                    </li>
                  ) : (
                    inPhase.map((s) => (
                      <StatusCard
                        key={s.id}
                        status={s}
                        name={nameOf(s)}
                        phase={phaseOf(s)}
                        role={roleOf(s)}
                        phaseOptions={phaseOptions}
                        setEdit={setEdit}
                        renaming={renamingId === s.id}
                        onRenamingChange={(on) => setRenamingId(on ? s.id : null)}
                        onRetire={statuses.length > 1 ? () => setRetiring(s) : undefined}
                        t={t}
                      />
                    ))
                  )}
                </PhaseBucket>
              )
            })}
          </div>

          {/* The lifted card while dragging — a faithful, static copy of the
              source card (sized to it, since the card is the measured node).
              Follows the pointer / snaps between buckets for keyboard; decoupled
              from the list so re-phasing on drop doesn't fight the drag. Inert:
              the overlay wrapper is pointer-events:none. */}
          <DragOverlay className="rounded-lg shadow-2xl ring-2 ring-primary/50">
            {(data) =>
              data ? (
                <StatusCardView
                  status={data.status as InternalStatus}
                  name={data.name as string}
                  phase={data.fromPhase as LifecyclePhase}
                  role={data.role as EngineRole}
                  phaseOptions={phaseOptions}
                  setEdit={() => {}}
                  t={t}
                />
              ) : null
            }
          </DragOverlay>
        </DndContext>
      )}

      <footer className="flex items-center justify-end gap-3 border-t border-border/60 pt-4">
        {message && (
          <span
            className={
              message.tone === 'error' ? 'text-xs text-toast-error-text' : 'text-xs text-muted'
            }
          >
            {message.text}
          </span>
        )}
        {blankName && (
          <span className="text-xs text-toast-error-text">
            {t('pages.pipelines.settings.rename.required', {
              defaultValue: 'A stage needs a name.',
            })}
          </span>
        )}
        {dirty && !message && !blankName && (
          <span className="text-xs text-muted">
            {t('pages.pipelines.settings.unsaved', {
              count: changed.length,
              defaultValue: '{{count}} unsaved change(s)',
            })}
          </span>
        )}
        <Button onClick={onSave} disabled={!dirty || saving || blankName}>
          {saving
            ? t('pages.pipelines.settings.saving', { defaultValue: 'Saving…' })
            : t('pages.pipelines.settings.save', { defaultValue: 'Save' })}
        </Button>
      </footer>

      {pipelineId && retiring && (
        <RetireStageDialog
          open
          onOpenChange={(next) => !next && setRetiring(null)}
          pipelineId={pipelineId}
          stage={retiring}
          siblings={statuses.filter((s) => s.id !== retiring.id)}
          onRetired={async (stageId) => {
            await statusesSwr.mutate()
            setEdits((prev) => {
              const next = { ...prev }
              delete next[stageId]
              return next
            })
            setRetiring(null)
          }}
        />
      )}

      {pipelineId && (
        <NewStageDialog
          open={addingStage}
          onOpenChange={setAddingStage}
          pipelineId={pipelineId}
          onCreated={() => statusesSwr.mutate()}
        />
      )}
    </div>
  )
}

function AddStageButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const { t } = useTranslation()
  return (
    <Button variant="primary" size="sm" onClick={onClick} className={className}>
      {t('pages.pipelines.settings.addStage.button', { defaultValue: 'Add stage' })}
    </Button>
  )
}

/** Six-dot drag affordance. Inline like the other SVGs in this package's
 *  primitives (no icon dependency). */
function GripIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="shrink-0"
    >
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  )
}

interface PhaseBucketProps {
  phase: LifecyclePhase
  color: string
  label: string
  count: number
  children: ReactNode
}

/**
 * A phase section that is also a drop target. The `<ul>` registers as a
 * droppable; `isOver` tints it while a card hovers. The left accent and counts
 * are unchanged from the pre-DnD layout.
 */
function PhaseBucket({ phase, color, label, count, children }: PhaseBucketProps) {
  const { setNodeRef, isOver } = useDroppable({ id: phase, data: { phase } })
  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center gap-2 pl-0.5">
        <span
          aria-hidden="true"
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground">
          {label}
        </h2>
        <span className="text-[11px] tabular-nums text-muted/70">{count}</span>
      </header>
      <ul
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 rounded-xl border border-border bg-background-secondary p-2 transition-colors',
          isOver && 'border-primary/70 bg-primary-light/20',
        )}
        style={{ boxShadow: `inset 3px 0 0 0 color-mix(in srgb, ${color} 55%, transparent)` }}
      >
        {children}
      </ul>
    </section>
  )
}

interface StatusCardProps {
  status: InternalStatus
  /** Effective (draft-or-server) name. */
  name: string
  /** Effective (draft-or-server) phase — also the drag's source bucket. `null` on an interaction board. */
  phase: LifecyclePhase | null
  /** Effective (draft-or-server) engine role. */
  role: EngineRole
  phaseOptions: { value: LifecyclePhase; label: string }[]
  setEdit: (id: StatusId, patch: StatusEdit) => void
  /** Name shown as an input instead of text. Absent for the overlay copy. */
  renaming?: boolean
  onRenamingChange?: (renaming: boolean) => void
  /** Absent when this stage cannot be retired — a pipeline keeps a last stage. */
  onRetire?: () => void
  t: TranslateFn
}

/** Handle wiring threaded from `useDraggable` into the card view. Omitted for
 *  the static copy rendered inside the drag overlay. */
interface StatusCardHandle {
  ref: (node: HTMLElement | null) => void
  attributes: DraggableAttributes
  listeners: UseDraggableResult['listeners']
  label: string
}

interface StatusCardViewProps extends StatusCardProps {
  /** Measured-node ref (the whole card) — sizes the drag overlay. */
  cardRef?: (node: HTMLElement | null) => void
  /** Grip-handle wiring; absent → static (overlay) copy with an inert grip. */
  handle?: StatusCardHandle
  /** Dim the source card in place while its overlay copy is being dragged. */
  isDragging?: boolean
}

/**
 * One status card, presentational. A grip handle (left) makes it draggable; the
 * role segmented control and the phase `<Select>` are unchanged and remain the
 * keyboard/touch fallback for re-phasing — DnD layers on top, it doesn't replace
 * them. Rendered both in place (with `handle`) and as the faithful static copy
 * inside the drag overlay (no `handle`, inert under the overlay's
 * `pointer-events:none`).
 */
function StatusCardView({
  status,
  name,
  phase,
  role,
  phaseOptions,
  setEdit,
  renaming = false,
  onRenamingChange,
  onRetire,
  t,
  cardRef,
  handle,
  isDragging = false,
}: StatusCardViewProps) {
  return (
    <li
      ref={cardRef}
      className={cn(
        'flex flex-col gap-2.5 rounded-lg border border-border/60 bg-background px-3 py-2.5 transition-colors hover:border-border-hover',
        isDragging && 'opacity-50',
      )}
    >
      {/* Name on its own line, full width — never truncated; statuses can differ
          only in their tail, so the whole name has to be readable. It turns into
          an input only while being renamed, so the wrapping text stays the
          resting state. */}
      <div className="flex items-start gap-2">
        <button
          ref={handle?.ref}
          type="button"
          aria-label={handle?.label}
          {...handle?.attributes}
          {...handle?.listeners}
          className="-ml-1 mt-px flex h-5 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted/50 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
        >
          <GripIcon />
        </button>
        <span className="mt-px font-mono text-[11px] text-muted/60">{status.position}</span>
        {renaming ? (
          <input
            autoFocus
            value={name}
            aria-invalid={name.trim().length === 0}
            aria-label={t('pages.pipelines.settings.rename.label', {
              name: status.name,
              defaultValue: 'Rename {{name}}',
            })}
            onChange={(e) => setEdit(status.id, { name: e.target.value })}
            onBlur={() => onRenamingChange?.(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEdit(status.id, { name: undefined })
              if (e.key === 'Escape' || e.key === 'Enter') onRenamingChange?.(false)
            }}
            className="min-w-0 flex-1 rounded border border-border bg-background-secondary/60 px-1.5 py-0.5 text-sm font-medium leading-snug text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-toast-error"
          />
        ) : (
          <button
            type="button"
            onClick={() => onRenamingChange?.(true)}
            title={t('pages.pipelines.settings.rename.label', {
              name,
              defaultValue: 'Rename {{name}}',
            })}
            className="-mx-1 min-w-0 flex-1 rounded px-1 text-left text-sm font-medium leading-snug text-foreground outline-none transition-colors hover:bg-background-secondary/70 focus-visible:ring-2 focus-visible:ring-ring"
          >
            {name}
          </button>
        )}
        {onRetire && (
          <button
            type="button"
            onClick={onRetire}
            aria-label={t('pages.pipelines.settings.retire.label', {
              name,
              defaultValue: 'Retire {{name}}',
            })}
            title={t('pages.pipelines.settings.retire.label', {
              name,
              defaultValue: 'Retire {{name}}',
            })}
            className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted/50 outline-none transition-colors hover:text-toast-error-text focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Controls below the name. Deterministic placement: stacked on mobile
          (role then phase, identical for every card), side-by-side from `sm`
          with phase pinned right — the row itself never wraps per-card, so cards
          can't disagree on where the phase select sits. The role pills still
          wrap *inside their own track* when space is tight. */}
      <div className="flex flex-col gap-2 pl-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {/* Axis B — what distribution does on entry. A segmented control: the
            bordered track bounds the pills so they read as one control and the
            active (filled) segment is unmistakably the value. */}
        <div className="flex items-center gap-2">
          <span className="w-9 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted/50">
            {t('pages.pipelines.settings.caption.role', { defaultValue: 'Role' })}
          </span>
          <div
            role="group"
            aria-label={t('pages.pipelines.settings.caption.role', { defaultValue: 'Role' })}
            className="inline-flex flex-wrap content-start items-center gap-1 rounded-xl border border-border/60 bg-background-secondary/70 p-1"
          >
            {ENGINE_ROLES.map((r) => {
              const active = role === r
              return (
                <button
                  key={r}
                  type="button"
                  aria-pressed={active}
                  title={t(`pages.pipelines.settings.role.${r}`, { defaultValue: r })}
                  onClick={() => setEdit(status.id, { engineRole: r })}
                  className={cn(
                    'cursor-pointer rounded-md px-2 py-1 text-[10px] font-semibold uppercase leading-none tracking-wide outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                    active
                      ? ROLE_ACTIVE[r]
                      : 'text-muted/70 hover:bg-background hover:text-foreground',
                  )}
                >
                  {t(`pages.pipelines.settings.roleShort.${r}`, { defaultValue: r })}
                </button>
              )
            })}
          </div>
        </div>

        {/* Axis A — which canonical phase this status rolls up to. */}
        <label className="flex items-center gap-2">
          <span className="w-9 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted/50">
            {t('pages.pipelines.settings.caption.phase', { defaultValue: 'Phase' })}
          </span>
          {phase === null ? (
            <span
              className="inline-flex h-8 min-w-[150px] items-center px-2 text-xs text-muted/60"
              title={t('pages.pipelines.settings.phase.none', {
                defaultValue: 'No lifecycle — an outcome, not a stage',
              })}
            >
              —
            </span>
          ) : (
            <Select<LifecyclePhase>
              value={phase}
              onValueChange={(v) => setEdit(status.id, { lifecyclePhase: v })}
              options={phaseOptions}
              triggerClassName="h-8 min-w-[150px] text-xs"
            />
          )}
        </label>
      </div>
    </li>
  )
}

/**
 * Live, draggable status card. Wires `useDraggable` to `StatusCardView`: the
 * whole card is the *measured* node (so the overlay mirrors it), while the grip
 * stays the activation + focus-restore target. The draggable `data` also carries
 * the status/role so the overlay can render a faithful copy.
 */
function StatusCard({
  status,
  name,
  phase,
  role,
  phaseOptions,
  setEdit,
  renaming,
  onRenamingChange,
  onRetire,
  t,
}: StatusCardProps) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: status.id,
    data: { fromPhase: phase, name, status, role },
    roleDescription: t('pages.pipelines.settings.dnd.roleDescription', {
      defaultValue: 'Draggable status. Press space or enter to pick up.',
    }),
  })
  return (
    <StatusCardView
      status={status}
      name={name}
      phase={phase}
      role={role}
      phaseOptions={phaseOptions}
      setEdit={setEdit}
      renaming={renaming}
      onRenamingChange={onRenamingChange}
      onRetire={onRetire}
      t={t}
      isDragging={isDragging}
      cardRef={setNodeRef}
      handle={{
        ref: setActivatorNodeRef,
        attributes,
        listeners,
        label: t('pages.pipelines.settings.dnd.handleLabel', {
          name,
          defaultValue: 'Drag {{name}} to another phase',
        }),
      }}
    />
  )
}
